import { EChartsOption } from 'echarts';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart, ScatterChart } from 'echarts/charts';
import {
  DatasetComponent,
  GraphicComponent,
  GridComponent,
  MarkAreaComponent,
  MarkLineComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsReactProps } from 'echarts-for-react/lib/types';
import { useCallback, useRef } from 'react';
import { useDebounceCallback, useResizeObserver } from 'usehooks-ts';
import { RESIZE_DEBOUNCE_MS } from '../../constants';
import { echartsTheme } from './echartsTheme';

export type EchartsWrapperProps = Partial<EChartsReactProps> & {
  option: EChartsOption;
};

/**
 * Register only the components we'll use. This should improve bundle size.
 * See echarts-for-react docs: https://www.npmjs.com/package/echarts-for-react
 */
echarts.use([
  CanvasRenderer,
  DatasetComponent,
  GridComponent,
  LineChart,
  MarkAreaComponent,
  MarkLineComponent,
  ScatterChart,
  TitleComponent,
  TooltipComponent,
  GraphicComponent,
]);

/**
 * On iOS, the chart gets stuck at the wider landscape width after the device
 * rotates. To fix this, we watch the container for size changes and force the
 * chart to resize whenever it changes. We apply this behavior on all
 * platforms for consistency.
 */
export const EchartsWrapper = (props: EchartsWrapperProps): JSX.Element => {
  const chartRef = useRef<ReactEChartsCore>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const resizeChart = useCallback((): void => {
    chartRef.current
      ?.getEchartsInstance()
      ?.resize({ width: 'auto', height: 'auto' });
  }, []);

  useResizeObserver({
    ref: containerRef,
    onResize: useDebounceCallback(resizeChart, RESIZE_DEBOUNCE_MS),
  });

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <ReactEChartsCore
        theme={echartsTheme}
        {...props}
        echarts={echarts}
        ref={chartRef}
      />
    </div>
  );
};
